import { getValidAccessToken } from "./authToken";

/**
 * Download a protected file to disk.
 *
 * Navigating with `window.location.href` cannot work for these URLs: the JWT
 * lives in localStorage, not a cookie, so the browser sends no Authorization
 * header and the API answers 401. Fetch the bytes with the token instead and
 * hand the resulting blob to a temporary anchor.
 */
export async function downloadFile(
  url: string,
  fallbackName = "download",
): Promise<void> {
  const token = await getValidAccessToken();

  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    let detail = "";
    try {
      const body: { message?: string } = await response.json();
      detail = body?.message ?? "";
    } catch {
      /* not a JSON error body */
    }
    throw new Error(detail || `Download failed (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filenameFrom(response) ?? fallbackName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Give the browser a moment to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  }
}

/** Read the server-supplied filename out of Content-Disposition, if present. */
function filenameFrom(response: Response): string | null {
  const header = response.headers.get("Content-Disposition");
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) return decodeURIComponent(utf8[1].trim());
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1].trim() : null;
}
